/*
 * From https://www.redblobgames.com/x/2145-spaceship-flyer/
 * Copyright 2021 Red Blob Games <redblobgames@gmail.com>
 * License: Apache-2.0 <http://www.apache.org/licenses/LICENSE-2.0.html>
 */

import createREGL from "../../js/regl";
import { earcut } from "earcut"; // typescript warning, can't find a way to fix it
import { vec4, mat3, mat4 } from "gl-matrix";

function debug(msg: string) { document.getElementById('debug').innerText = msg; }
const canvas = document.querySelector("canvas");

/* Simulation */

function degreesToRadians(deg: number) { return deg * Math.PI/180; }

const Physics = {
    lossPerSec: 0.06,
    frictionBase: 100,
};

class Ship {
    x: number = 0;
    y: number = 0;
    facingθ: number = degreesToRadians(90);
    vx: number = 0;
    vy: number = 0;
    vθ: number = 0;
    maxSpeed: number = 100;

    hullShapeVertices: number[];
    hullShapeTriangulation: number[];

    mass: number = 1e-9;
    momentOfInertia: number = 1e-9;

    constructor(hullShape: number[]) {
        this.hullShapeVertices = hullShape;
        this.hullShapeTriangulation = earcut(hullShape);
        this.updateMass();
    }

    updateMass() {
        this.mass = 100;
        this.momentOfInertia = 250;
    }

    applyFriction(dt: number) {
        let slowdown = 1.0 - dt * Physics.lossPerSec;
        let fraction = 1.0 - dt * Math.hypot(this.vx, this.vy) / Physics.frictionBase;
        this.vx *= slowdown * fraction;
        this.vy *= slowdown * fraction;
        // TODO: I want rotation to slow down at a different rate
        // this.vθ *= slowdown * (1.0 - Math.abs(this.vθ));
        
        // TODO: quadratic provides the maximum speed; linear I'm not
        // sure how useful it is; if I want it to come to a halt at
        // low speeds I need to add constant friction

        /* Math:
           
           Given some `acceleration`, velocity will increase by
           acceleration * dt each tick, and then get multiplied by
           slowdown * fraction. In the steady state, (1 - slowdown *
           fraction) * velocity = acceleration * dt. Solve:
           
           velocity * (1 - slowdown * fraction) = dt*acceleration
           velocity * (1 - (1 - dt*resistance) * (1 - dt*velocity/K)) = dt*acceleration
           velocity - (1 - dt*resistance) * (velocity - dt*velocity^2/K) = dt*acceleration
           velocity - velocity + dt*resistance * velocity + (1 - dt*resistance)*(dt/K * velocity^2) = dt*acceleration
           dt*resistance * velocity - (1 - dt*resistance)*(dt/K * velocity^2) = dt*acceleration
           resistance * velocity - (1 - dt*resistance)*(1/K * velocity^2) = acceleration
           (1 - dt*resistance)/K * velocity^2 + resistance * velocity - acceleration = 0
           
           Quadratic equation, with a = (1-dt*resistance)/K, b = resistance, c = -acceleration
           
           Solution: 2c / (-b ± sqrt(b² - 4ac))
           
           = -2*acceleration / (-resistance ± sqrt(resistance² + (1-dt*resistance)*4/K * acceleration))
           
           Well, that's not very enlightening. But for tuning, it means I now have a link between
           acceleration (one configurable parameter) and resistance (the other configurable parameter)
           and the maximum velocity.
           
           In addition to the steady state, I want to tune based on
           how quickly the ship accelerates to 90% of its maximum. To
           make the ship accelerate quicker, I need to increase
           acceleration and decrease K.
        */
    }
}

/* Graphics */

const regl = createREGL({
    canvas,
    extensions: ['OES_standard_derivatives', 'angle_instanced_arrays', 'EXT_blend_minmax'],
});

interface Uniforms {
    u_color: vec4;
    u_view: mat3;
    u_projection: mat4;
}

interface Attributes {
    a_world_position: number[];
}

interface Props {
    u_color: vec4;
    u_view: mat3;
    u_inverse_view: mat3;
    a_world_position: number[];
    elements: number[];
}

const drawGrid = regl<any, any, any>({
    frag: `
precision highp float;
uniform float u_minor_spacing, u_major_spacing;
varying vec2 v_world_position;
void main() {
    vec2 f1 = abs(0.5 - fract(v_world_position / u_minor_spacing));
    vec2 f2 = abs(0.5 - fract(v_world_position / u_major_spacing));
    vec4 base_color = vec4(0.21, 0.25, 0.38, 1);
    gl_FragColor = base_color + vec4(
                        -0.1*smoothstep(0.09, 0.0, min(f2.x, f2.y)),
                        0.1*smoothstep(0.06, 0.0, min(f1.x, f1.y)),
                        0,
                        0);
}`,

    vert: `
precision highp float;
attribute vec2 a_screen_position;
varying vec2 v_world_position;
uniform mat3 u_inverse_view;
uniform mat4 u_projection;
void main() {
    v_world_position = (u_inverse_view * vec3(a_screen_position, 1)).xy;
    gl_Position = u_projection * vec4(a_screen_position, 1, 1);
}`,

    depth: { enable: false, func: 'less' },
    blend: { enable: true, func: { src: 'one', dst: 'one minus src alpha' }, },

    count: 3,
    attributes: {
        a_screen_position: [[-4, -4], [+4, -4], [0, +4]],
    },
    uniforms: {
        u_inverse_view: regl.prop<Props, 'u_inverse_view'>('u_inverse_view'),
        u_projection: mat4.create(),
        u_minor_spacing: 10,
        u_major_spacing: 50,
    },
});

const drawShape = regl<Uniforms, Attributes, Props>({
    frag: `
precision highp float;
uniform vec4 u_color;
void main () {
    gl_FragColor = u_color;
}`,
    
    vert: `
precision highp float;
attribute vec2 a_world_position;
uniform mat3 u_view;
uniform mat4 u_projection;
varying vec3 v_color;
void main () {
    vec3 screen_position = u_view * vec3(a_world_position, 1);
    gl_Position = u_projection * vec4(screen_position, 1);
}`,

    depth: { enable: false, func: 'less' },
    blend: { enable: true, func: { src: 'one', dst: 'one minus src alpha' }, },

    elements: regl.prop<Props, 'elements'>('elements'),
    attributes: {
        a_world_position: regl.prop<Props, 'a_world_position'>('a_world_position'),
    },
    uniforms: {
        u_color: regl.prop<Props, 'u_color'>('u_color'),
        u_view: regl.prop<Props, 'u_view'>('u_view'),
        u_projection: mat4.create(),
    },
});

const drawSmoke = regl<any, any, any>({
    frag: `
precision highp float;
varying float alpha;
void main() {
    float len = 0.5 - length(gl_PointCoord.xy - 0.5);
    if (len < 0.0) {
        discard;
    }
    vec3 color = mix(vec3(0.50, 0.45, 0.30), vec3(2, 2, 2), sqrt(alpha));
    gl_FragColor = vec4(color, alpha * 10.0 * len);
}`,

    vert: `
precision highp float;
attribute vec4 a_world_positiontime;
uniform mat3 u_view;
uniform mat4 u_projection;
uniform float u_timestamp;
varying float alpha;
void main() {
    vec3 screen_position = u_view * vec3(a_world_positiontime.xy, 1);
    float radius = 10.0 * (1.0 + 10.0 * (u_timestamp - a_world_positiontime.z));
    alpha = 50.0 * a_world_positiontime.a / (radius * radius);
    gl_PointSize = radius;
    gl_Position = u_projection * vec4(screen_position, 1);
}`,

    depth: { enable: false },
    blend: {
        enable: true,
        func: { src: 'src alpha', dst: 'one minus src alpha' },
        equation: { rgb: 'add', alpha: 'max', },
    },

    primitive: 'points',
    count: regl.prop<any, 'count'>('count'),
    attributes: {
        a_world_positiontime: regl.prop<any, 'a_world_positiontime'>('a_world_positiontime'),
    },
    uniforms: {
        u_timestamp: regl.prop<any, 'u_timestamp'>('u_timestamp'),
        u_view: regl.prop<any, 'u_view'>('u_view'),
        u_projection: mat4.create(),
    },
});

const SMOKE_TRAIL_DURATION_SEC = 5;
let _drawPending = undefined;
let _smoke = [];
function draw(player: Ship) {
    let timestamp_sec = performance.now() * 1e-3; // TODO: should be simulation time, not real time
    _drawPending = undefined;
    regl.clear({ color: [0, 0, 0, 1], depth: 1 });

    let view = mat3.create();
    mat3.scale(view, view, [0.01, 0.01]);
    if ((document.querySelector("#camera-rotate") as HTMLInputElement).checked) mat3.rotate(view, view, Math.PI/2 - player.facingθ);
    mat3.translate(view, view, [-player.x, -player.y]);
    
    drawGrid({
        u_inverse_view: mat3.invert(mat3.create(), view),
    });

    // Smoke trail from each thruster, positioned at (-1, 4) and (-1,
    // -4). First calculate the world x,y for the thruster locations
    let smokeDx1 = -1 * Math.cos(player.facingθ) - 4 * Math.sin(player.facingθ);
    let smokeDy1 = -1 * Math.sin(player.facingθ) + 4 * Math.cos(player.facingθ);
    let smokeDx2 = -1 * Math.cos(player.facingθ) + 4 * Math.sin(player.facingθ);
    let smokeDy2 = -1 * Math.sin(player.facingθ) - 4 * Math.cos(player.facingθ);
    let smokeStrength = Math.hypot(player.vx, player.vy) / player.maxSpeed + Math.abs(player.vθ) / 8;
    _smoke.push([player.x + smokeDx1, player.y + smokeDy1, timestamp_sec, smokeStrength]);
    _smoke.push([player.x + smokeDx2, player.y + smokeDy2, timestamp_sec, smokeStrength]);
    while (_smoke.length > 0 && timestamp_sec - _smoke[0][2].timestamp > SMOKE_TRAIL_DURATION_SEC) _smoke.shift();
    drawSmoke({
        u_view: view,
        u_timestamp: timestamp_sec,
        a_world_positiontime: _smoke,
        count: _smoke.length,
    });

    let transform = mat3.clone(view);
    mat3.translate(transform, transform, [player.x, player.y]);
    mat3.rotate(transform, transform, Math.PI/2 + player.facingθ);
    drawShape({
        u_color: [1, 0.8, 0, 1],
        u_view: transform,
        a_world_position: player.hullShapeVertices,
        elements: player.hullShapeTriangulation,
    });
}

/* Input */
let KeyboardTracker = {
    // On AZERTY, event.key will be 'w' but event.code will be 'KeyW'
    // When shifted, event.key will be 'W' but event.code will be 'KeyW'
    // I'm using code instead of key

    keysDown: {
        KeyW: false,
        KeyS: false,
        KeyA: false,
        KeyD: false,
        KeyQ: false,
        KeyE: false,
    },

    keyDown(event: KeyboardEvent) {
        if (this.keysDown[event.code] !== undefined) {
            this.keysDown[event.code] = true;
            event.preventDefault();
        }
    },

    keyUp(event: KeyboardEvent) {
        if (this.keysDown[event.code] !== undefined) {
            this.keysDown[event.code] = false;
            event.preventDefault();
        }
    },
}


class Simulation {
    running: number = undefined;
    lastTimestamp: number = null;

    constructor (public player: any, public keyboardTracker: any) {
    }
    
    start() {
        if (this.running) return;
        let firstTime = true;
        this.lastTimestamp = Date.now();
        this.running = setInterval(() => {
            let currentTimestamp = Date.now();
            let dt = (currentTimestamp - this.lastTimestamp) * 1e-3;
            this.lastTimestamp = currentTimestamp;
            if (firstTime || this.simulateStep(dt)) {
                if (!_drawPending) { _drawPending = requestAnimationFrame(() => draw(player)); }
            }
            firstTime = false;
        }, 5);
    }

    stop() {
        if (!this.running) return;
        clearInterval(this.running);
        this.running = undefined;
        this.lastTimestamp = null;
    }

    /* returns true if anything needs to be redrawn */
    simulateStep(dt: number): boolean {
        // +V is forwards in ship space (== facing)
        const player = this.player;
        const { keysDown } = this.keyboardTracker;
        let du = (keysDown.KeyQ ? -1/2 : 0) + (keysDown.KeyE ? +1/2 : 0);
        let dv = (keysDown.KeyW ? +1 : 0) + (keysDown.KeyS ? -1/2 : 0);
        let dθ = (keysDown.KeyA ? -1 : 0) + (keysDown.KeyD ? +1 : 0);

        let facingX = Math.cos(player.facingθ),
            facingY = Math.sin(player.facingθ);
        let dx = (facingX * dv * player.maxSpeed + facingY * du * player.maxSpeed);
        let dy = (facingY * dv * player.maxSpeed - facingX * du * player.maxSpeed);
        
        player.applyFriction(dt);

        // TODO: magic numbers
        const acceleration = 0.9;
        let ax = (dx - player.vx) * acceleration;
        let ay = (dy - player.vy) * acceleration;
        let aθ = (dθ - player.vθ) * acceleration * 4; // higher number gives a quicker stop

        player.vx += ax * dt;
        player.vy += ay * dt;
        player.x += (player.vx + 0.5 * ax * dt) * dt;
        player.y += (player.vy + 0.5 * ay * dt) * dt;
        player.vθ += Math.PI / 2 * aθ * dt;

        const epsilon = 1e-6; // too high and velocity clamps to 0 when we're trying to accelerate past 0
        if (Math.abs(player.vx) < epsilon) player.vx = 0;
        if (Math.abs(player.vy) < epsilon) player.vy = 0;
        if (Math.abs(player.vθ) < epsilon) player.vθ = 0;

        if (player.vx === 0 && player.vy === 0 && player.vθ === 0) return false;

        // TODO: magic number
        player.facingθ -= player.vθ * dt * 5;
        return true;
    }
}

const player = new Ship(
    [1, -7, -1, -7, -2, 0, -5, 0, -5, 2,
        -2, 2.5, -2, 3, 2, 3, 2, 2.5, 5, 2, 5, 0, 2, 0]
);
let simulation = new Simulation(player, KeyboardTracker);

const instructions = document.getElementById('focus-instructions');
canvas.setAttribute('tabindex', "1");
canvas.addEventListener('keydown', event => KeyboardTracker.keyDown(event));
canvas.addEventListener('keyup', event => KeyboardTracker.keyUp(event));
canvas.addEventListener('blur', () => { simulation.stop(); instructions.classList.add('visible'); });
canvas.addEventListener('focus', () => { simulation.start(); instructions.classList.remove('visible'); })
canvas.focus();
simulation.start();

/*
let vy = [], x = [];
for (Physics.frictionBase = 1.0; Physics.frictionBase < 100.0; Physics.frictionBase += 1.0) {
    let fakePlayer = new Ship([]);
    let forwards = new Simulation(fakePlayer, {keysDown: {KeyW: true}});
    for (let i = 0; i < 50; i++) {
        forwards.simulateStep(0.05);
    }
    x.push(Physics.frictionBase.toFixed(0));
    vy.push(fakePlayer.vy);
}
new Chart(document.getElementById('chart-friction').getContext('2d'), {
    type: 'line',
    data: {
        labels: x,
        datasets: [{
            label: 'Friction',
            data: vy,
            borderColor: 'hsl(240, 50%, 50%)',
        }],
    },
});

Physics.frictionBase = 100.0;
x = []; vy = [];
let vy2 = [];
for (Physics.lossPerSec = 0.0; Physics.lossPerSec < 10.0; Physics.lossPerSec += 0.1) {
    let fakePlayer = new Ship([]);
    let forwards = new Simulation(fakePlayer, {keysDown: {KeyW: true}});
    for (let i = 0; i < 50; i++) {
        forwards.simulateStep(0.05);
    }
    x.push(Physics.lossPerSec.toFixed(2));
    vy.push(fakePlayer.vy);
}
Physics.frictionBase = 1000.0;
for (Physics.lossPerSec = 0.0; Physics.lossPerSec < 10.0; Physics.lossPerSec += 0.1) {
    let fakePlayer = new Ship([]);
    let forwards = new Simulation(fakePlayer, {keysDown: {KeyW: true}});
    for (let i = 0; i < 50; i++) {
        forwards.simulateStep(0.05);
    }
    vy2.push(fakePlayer.vy);
}
new Chart(document.getElementById('chart-loss').getContext('2d'), {
    type: 'line',
    data: {
        labels: x,
        datasets: [{
            label: 'Loss per Sec',
            data: vy,
            borderColor: 'hsl(170, 50%, 50%)',
        }, {
            label: 'with low friction base',
            data: vy2,
            borderColor: 'hsl(100, 50%, 30%)',
        }],
    },
});
*/

// What I really want to know is how quickly speed increases over
// time, maybe with a metric like how long to max speed, and how long
// to half max speed

Physics.lossPerSec = 0.06;
Physics.frictionBase = 100;
let fakePlayer = new Ship([]);
let forwards = new Simulation(fakePlayer, {keysDown: {KeyW: true}});
x = []; vy = [];
for (let i = 0; i < 50; i++) {
    x.push(i);
    vy.push(fakePlayer.vy);
    forwards.simulateStep(0.05);
}
player.maxSpeed = 1.6 * fakePlayer.vy;
console.log(fakePlayer.vx, fakePlayer.vy);
new Chart(document.getElementById('chart-acceleration').getContext('2d'), {
    type: 'line',
    data: {
        labels: x,
        datasets: [{
            label: 'Speed',
            data: vy,
            borderColor: 'hsl(0, 50%, 50%)',
        }],
    },
});
